
import React, { useState } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableCell, TableBody } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useForm } from "react-hook-form";
import type { Database } from "@/integrations/supabase/types";

type MarketingProject = Database['public']['Tables']['marketing_projects']['Row'];

interface ProjectFormValues {
  name: string;
}

interface ProductListProps {
  projects: MarketingProject[];
  selectedProject: MarketingProject | null;
  onSelectProject: (project: MarketingProject) => void;
  onCreateProject: (values: ProjectFormValues) => Promise<void>;
  onEditProject: (project_id: string, newName: string) => Promise<void>;
  onDeleteProject: (project_id: string) => Promise<void>;
}

export const ProductList: React.FC<ProductListProps> = ({
  projects,
  selectedProject,
  onSelectProject,
  onCreateProject,
  onEditProject,
  onDeleteProject
}) => {
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const projectTableRef = React.useRef<HTMLDivElement>(null);

  const projectForm = useForm<ProjectFormValues>({
    defaultValues: {
      name: ''
    }
  });

  const handleCreateProject = async (values: ProjectFormValues) => {
    await onCreateProject(values);
    setIsNewProjectOpen(false);
    projectForm.reset();
  };

  const handleEditProject = async (project_id: string, newName: string) => {
    await onEditProject(project_id, newName);
    setEditingProjectId(null);
    setEditName('');
  };

  const generateEmptyRows = (count: number, columnCount: number, startIndex: number) => {
    return Array(count).fill(null).map((_, index) => (
      <TableRow key={`empty-${index}`}>
        <TableCell className="text-[11px] text-center text-white">{startIndex + index + 1}</TableCell>
        {Array(columnCount - 2).fill(null).map((_, colIndex) => (
          <TableCell key={`empty-${index}-${colIndex}`} className="text-[11px] text-white">-</TableCell>
        ))}
        <TableCell className="text-right text-white">-</TableCell>
      </TableRow>
    ));
  };

  const calculateEmptyRowCount = (existingRowCount: number): number => {
    const maxVisibleRows = 5;
    return Math.max(maxVisibleRows - existingRowCount, 0);
  };

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 h-[265px] px-[12px] py-[15px]">
      <div className="flex justify-between items-center mb-2">
        <h2 className="font-semibold text-base text-white">Products or services (to evaluate)</h2>
        <Dialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-primary hover:bg-primary/90 text-slate-950 text-xs font-normal">
              <Plus className="w-4 h-4 mr-2" />
              New item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Item</DialogTitle>
            </DialogHeader>
            <Form {...projectForm}>
              <form onSubmit={projectForm.handleSubmit(handleCreateProject)} className="space-y-4">
                <FormField
                  control={projectForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product or Service Name</FormLabel>
                      <FormControl>
                        <Input {...field} className="border-primary/20" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex justify-start">
                  <Button type="submit" className="inline-flex bg-primary hover:bg-primary/90 text-black">
                    Create New Item
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
      <div ref={projectTableRef} className="overflow-auto rounded-md border border-primary/20 h-[calc(100%-40px)]">
        {projects.length === 0 ? (
          <div className="flex items-center justify-center h-full text-white">
            Add new items first
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-primary/5">
                <TableHead className="text-[11px] text-white font-semibold text-center">SN</TableHead>
                <TableHead className="text-[11px] text-white font-semibold">Name</TableHead>
                <TableHead className="text-[11px] text-white font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((project, index) => (
                <TableRow 
                  key={project.project_id} 
                  className={`cursor-pointer ${selectedProject?.project_id === project.project_id ? "bg-white text-black hover:bg-white" : "text-white hover:bg-primary/5"}`}
                  onClick={() => onSelectProject(project)}
                >
                  <TableCell className={`text-[11px] text-center ${selectedProject?.project_id === project.project_id ? "text-black" : "text-white"}`}>
                    {index + 1}
                  </TableCell>
                  <TableCell className={`text-[11px] ${selectedProject?.project_id === project.project_id ? "text-black" : "text-white"}`}>
                    {editingProjectId === project.project_id ? (
                      <form onSubmit={(e) => {
                        e.preventDefault();
                        handleEditProject(project.project_id, editName);
                      }}>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-6 text-[11px] text-primary bg-black border border-primary"
                          autoFocus
                          onBlur={() => {
                            setEditingProjectId(null);
                            setEditName("");
                          }}
                        />
                      </form>
                    ) : (
                      project.name
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-6 w-6 p-0 hover:bg-primary/10 ${selectedProject?.project_id === project.project_id ? "text-black" : "text-white"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingProjectId(project.project_id);
                          setEditName(project.name);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive ${selectedProject?.project_id === project.project_id ? "text-black" : "text-white"}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-black">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-[#ea384c]">Delete Project</AlertDialogTitle>
                            <AlertDialogDescription className="text-white">
                              Are you sure you want to delete this project? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteProject(project.project_id);
                              }}
                              className="bg-primary"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {generateEmptyRows(calculateEmptyRowCount(projects.length), 3, projects.length)}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
